import { initializeApp } from '@firebase/app';
import { getAuth, GoogleAuthProvider } from '@firebase/auth';
import { getFirestore, doc, deleteDoc, getDoc, setDoc, collectionGroup, query, where, limit, collection, getDocs, updateDoc } from '@firebase/firestore';
import { getStorage } from '@firebase/storage';
import { getFunctions, httpsCallable } from '@firebase/functions';
import sanitizeHtml from 'sanitize-html';
import format from 'string-template';
import photoicon from './images/photoicon.png';
import photoaddicon from './images/photoaddicon.png';
import nophotoicon from './images/nophotoicon.png';
/**
 * The common utility class
 *
 * @class Common
 * @static
 */
export class Common {
    /**
     * Initializes the common utility class
     *
     * @static
     * @param {boolean} v2 - The flag indicating whether the application is in version 2
     * @param {Environments} env - The current environment
     * @memberof Common
     */
    static init(v2, env) {
        if (!Common.res)
            throw new Error('Common.res must be provided');
        Common.v2 = v2;
        Common.env = env;
        Common.constConfigs = Common.res.constConfigs[Common.env];
        const firebaseConfig = Common.res.firebaseConfigs[Common.env];
        const app = initializeApp(firebaseConfig);
        Common.firebase = {
            firebaseConfig: firebaseConfig,
            app: app,
            auth: getAuth(app),
            provider: new GoogleAuthProvider(),
            db: getFirestore(),
            storage: getStorage(),
            functions: getFunctions()
        };
        Common.db = Common.firebase.db;
        Common.notifyForRequestFunc = Common.env == "dev" /* Environments.dev */ ? null : httpsCallable(Common.firebase.functions, 'notifyForRequest');
    }
    /**
     * Checks if a module is loaded
     *
     * @static
     * @param {Modules} moduleName - The name of the module
     * @returns {boolean} - Whether the module is loaded
     * @memberof Common
     */
    static moduleLoaded(moduleName) {
        return Common.modules.has(moduleName);
    }
    static async module(moduleName) {
        if (!Common.moduleLoaded(moduleName))
            await Common.loadModule(moduleName);
        return Common.modules.get(moduleName);
    }
    static moduleOrNull(moduleName) {
        if (!Common.moduleLoaded(moduleName))
            return null;
        return Common.modules.get(moduleName);
    }
    /**
     * Loads a module
     *
     * @static
     * @param {Modules} moduleName - The name of the module
     * @returns {Promise<void>}
     * @memberof Common
     */
    static async loadModule(moduleName) {
        const module = await import(`./modules/${moduleName}.js`);
        Common.modules.set(moduleName, new module.default());
    }
    /**
     * Clears all modules
     *
     * @static
     * @returns {Promise<void>}
     * @memberof Common
     */
    static async clearAllModules() {
        await Promise.all(Array.from(Common.modules.values()).map(async (module) => await module.clear()));
    }
    static mainId(id) {
        return id ? (Array.isArray(id) ? id[0] : id) : null;
    }
    // ------------------------------ util ------------------------------
    /**
     * This function is a shorthand for the ternary operator. If the `condition` is true, it returns `iftrue`; otherwise, it returns `iffalse`.
     *
     * @template T The type of the return value.
     *
     * @param condition The condition that determines which value to return.
     * @param iftrue The value returned if the condition is true.
     * @param iffalse The value returned if the condition is false.
     *
     * @returns T The value returned depends on the value of the `condition`.
     */
    static iif(condition, iftrue, iffalse) {
        return condition ? iftrue : iffalse;
    }
    /**
     * Check whether the current user has permission to access the specified module.
     *
     * @param moduleName The name of the module.
     *
     * @returns boolean Returns true if the user has permission to access the specified module; otherwise, false.
     */
    static accessAllowed(moduleName) {
        if (moduleName == "service" /* Modules.service */)
            return Common.user.userAdmin > 1;
        if (moduleName == "search" /* Modules.search */)
            return Common.user.userAdmin >= 0 && !Common.user.userOperator && Common.env != "dev" /* Environments.dev */;
        if (moduleName == "report" /* Modules.report */)
            return Common.user.userAdmin > 0;
        // if (moduleName == Modules.ingroups) return Common.user.userAdmin > 0;
        if (moduleName == "mappings" /* Modules.mappings */)
            return Common.user.userAdmin > 0;
        if (moduleName == "prompts" /* Modules.prompts */)
            return Common.user.userAdmin > 0;
        if (moduleName == "testperfect" /* Modules.testperfect */)
            return Common.user.userAdmin > 0;
        if (moduleName == "login" /* Modules.login */)
            return true;
        if (moduleName == "records" /* Modules.records */)
            return Common.user.userAdmin >= 0 && !Common.user.userOperator;
        return Common.user.userAdmin >= 0;
    }
    /**
     * Get the `HTMLInputElement` with the specified element id.
     *
     * @param elemId The id of the element.
     *
     * @returns HTMLInputElement | null The `HTMLInputElement` with the specified element id or null if not found.
     */
    static getInputElement(elemId) {
        return document.getElementById(elemId);
    }
    /**
     * Get the `HTMLImageElement` with the specified element id.
     *
     * @param elemId The id of the element.
     *
     * @returns HTMLImageElement | null The `HTMLImageElement` with the specified element id or null if not found.
     */
    static getImageElement(elemId) {
        return document.getElementById(elemId);
    }
    /**
     * Get the `HTMLTableElement` with the specified element id.
     *
     * @param elemId The id of the element.
     *
     * @returns HTMLTableElement | null The `HTMLTableElement` with the specified element id or null if not found.
     */
    static getTableElement(elemId) {
        return document.getElementById(elemId);
    }
    /**
     * Get the `checked` value of the `HTMLInputElement`.
     *
     * @param elem The `HTMLInputElement` to get the value from.
     *
     * @returns boolean The `checked` value of the `HTMLInputElement`. Returns `false` if `elem` is `null`.
     */
    static getCheckValue(elem) {
        return elem ? elem.checked : false;
    }
    /**
     * Display or hide an element.
     *
     * @param elemId The id of the element or the element itself.
     * @param bDisplay `true` to display the element, `false` to hide it.
     * @param display The value of the `display` style property. Can be 'inline', 'inline-block', 'block' or 'table'. Default is 'block'.
     */
    static displayElem(elemId, bDisplay, display) {
        const elem = typeof elemId === 'string' ? document.getElementById(elemId) : elemId;
        if (!elem || !elem.style)
            return;
        elem.style.display = bDisplay ? (display || 'block') : 'none';
    }
    /**
     * Show or hide the busy indicator.
     *
     * @param bShow `true` to show the busy indicator, `false` to hide it.
     */
    static showBusy(bShow) {
        Common.displayElem('busyDiv', bShow);
    }
    /**
     * Show or hide the popup message.
     *
     * @param text text string to show on the popup, empty text string hides the popup
     */
    static showPopupMessage(text) {
        Common.toElem(text, 'messageSpan');
        Common.displayElem('popupMessageDiv', !!text);
    }
    /**
    * static method which gets data from DOM element and assign it to json
    * @static
    * @template T extends DocumentData
    * @template K extends keyof T
    * @param {T} json - json object
    * @param {K} name - name of the property
    * @param {string} elemId - DOM element id
    * @param {'' | 'number'} [convert=''] - convert data to number or not
    * @memberof Common
    */
    static fromElem(json, name, elemId, convert = '') {
        const elem = document.getElementById(elemId);
        if (!elem)
            return;
        if (elem.tagName == 'SPAN') {
            if (convert == 'number')
                json[name] = (elem.innerText ? parseFloat(elem.innerText.replace(',', '.')) : 0);
            else
                json[name] = (elem.innerText || '');
            return;
        }
        const input = elem;
        if (!input)
            return;
        if (input.type == 'checkbox') {
            json[name] = input.checked;
            return;
        }
        if (input.type == 'number') {
            json[name] = (input.value ? parseFloat(input.value.replace(',', '.')) : 0);
            return;
        }
        if (convert == 'number')
            json[name] = (input.value ? parseFloat(input.value.replace(',', '.')) : 0);
        else if (convert == 'numberIfExists') {
            const v = (input.value && !isNaN(+input.value) ? parseFloat(input.value.replace(',', '.')) : void 0);
            if (typeof v !== 'undefined')
                json[name] = v;
        }
        else
            json[name] = (input.value || '');
    }
    static getFromElem(elemId, convert = '') {
        const json = {};
        Common.fromElem(json, 'field', elemId, convert);
        return json.field;
    }
    /**
    * static method which assigns data from json to DOM element
    * @static
    * @param {(string | boolean | number | undefined)} val - value from json
    * @param {string} elemId - DOM element id
    * @memberof Common
    */
    static toElem(val, elemId) {
        const elem = document.getElementById(elemId);
        if (!elem)
            return;
        if (elem.tagName == 'SPAN' || elem.tagName == 'DIV') {
            elem.innerText = val || '';
            return;
        }
        if (elem.tagName == 'IMG') {
            const img = elem;
            if (!img)
                return;
            img.removeAttribute('src');
            img.src = val || nophotoicon;
            return;
        }
        const input = elem;
        if (!input)
            return;
        if (input.type == 'checkbox') {
            input.checked = val != null && typeof val !== 'undefined' && val != false;
            return;
        }
        if (input.type == 'number') {
            input.value = (typeof val === 'undefined') ? '' : (typeof val === 'number') && isNaN(val) ? '' : String(val);
            return;
        }
        input.value = val ? String(val) || '' : '';
    }
    /**
    * static method which gets data from DOM element and assign it to json
    * @static
    * @template T extends DocumentData
    * @template K extends keyof T
    * @param {T} json - json object
    * @param {K} name - name of the property
    * @param {string} elemId - DOM element id
    * @memberof Common
    */
    static fromElemArr(json, name, elemId, bLabels = false) {
        const elem = document.getElementById(elemId);
        if (!elem)
            return;
        const arr = [];
        const inputs = elem.getElementsByTagName('input');
        for (let i = 0; i < inputs.length; ++i) {
            const input = inputs[i];
            if (input.type != 'checkbox')
                continue;
            if (input.checked)
                arr.push(bLabels ? input.parentElement?.innerText ?? '?' : input.title);
        }
        const selects = elem.getElementsByTagName('select');
        for (let i = 0; i < selects.length; ++i) {
            const select = selects[i];
            if (select.value)
                arr.push(bLabels ? `${select.title} ${select.options[select.selectedIndex].text}` : select.value);
        }
        json[name] = arr;
    }
    /**
    * static method which assigns data from json to DOM element
    * @static
    * @param {(string[] | undefined)} arr - value from json
    * @param {string} elemId - DOM element id
    * @memberof Common
    */
    static toElemArr(arr, elemId) {
        const elem = document.getElementById(elemId);
        if (!elem)
            return;
        const inputs = elem.getElementsByTagName('input');
        for (let i = 0; i < inputs.length; ++i) {
            const input = inputs[i];
            if (input.type != 'checkbox')
                continue;
            input.checked = typeof arr !== 'undefined' && arr.includes(input.title);
        }
        const selects = elem.getElementsByTagName('select');
        for (let i = 0; i < selects.length; ++i) {
            const select = selects[i];
            select.value = '';
            for (let j = 0; j < select.options.length; ++j) {
                const value = select.options[j].value;
                if (value && typeof arr !== 'undefined' && arr.includes(value))
                    select.value = value;
            }
        }
    }
    /**
     * @static
     * @param {ShortProductData} data
     * @return {string}
     */
    static getProductName(data) {
        if (data.title && data.brand)
            return `${data.brand}, ${data.title}`;
        if (data.title)
            return `${Common.res.lblNoBrand}, ${data.title}`;
        if (!data.productID)
            return '';
        return data.productID;
    }
    static getProductName2(brand, title) {
        if (title && brand)
            return `${brand}, ${title}`;
        if (title)
            return `${Common.res.lblNoBrand}, ${title}`;
        return '';
    }
    /**
     * @static
     * @param {string} elemId
     * @param {string[] | MapOfStrings | HierarchicalMapOfStrings} list
     * @param {boolean} bSelect
     * @param {string} [textElemId='']
     * @param {boolean} [bList=false]
     */
    static buildCheckboxes(elemId, list, bSelect, textElemId = '', bList = false, bSort = false) {
        function childrenSortFunc(item1, item2) {
            const a = item1.includes('Harsh') || item1.includes('Heavier') ? 'z' : item1;
            const b = item2.includes('Harsh') || item2.includes('Heavier') ? 'z' : item2;
            return a.localeCompare(b);
        }
        const elem = document.getElementById(elemId);
        if (!elem)
            return;
        const buildCheckbox = (cnt, item, title) => Common.elem('label', { class: 'checkbox-container inline small' }, [
            Common.text(item),
            Common.elem('input', { id: `${elemId}_${cnt}`, title: title, type: 'checkbox' }),
            Common.elem('span', { class: 'checkmark inline small' }),
        ]);
        const items = Array.isArray(list) ?
            Common.sortArray(list, bSort).map((item, index) => bList ? Common.elem('li', {}, buildCheckbox(index, item, item)) : buildCheckbox(index, item, item))
            :
                Common.sortList(list, bSort).map((key, index) => {
                    const item = list[key];
                    if (typeof item === 'string')
                        return bList ? Common.elem('li', {}, buildCheckbox(index, item, key)) : buildCheckbox(index, item, key);
                    const keys = Object.keys(item).sort((key1, key2) => { return childrenSortFunc(item[key1], item[key2]); });
                    const select = Common.elem('div', { style: 'break-inside: avoid-column;' }, [
                        Common.elem('label', { class: 'select-label small' }, Common.text(key)),
                        Common.elem('select', { id: `${elemId}_${index}`, class: 'select-elem small', title: key }, [
                            Common.elem('option', { value: '', class: 'select-item small' }, Common.text('')),
                            ...keys.map((child) => Common.elem('option', { value: child, class: 'select-item small' }, Common.text(item[child]))),
                        ]),
                    ]);
                    return bList ? Common.elem('li', {}, select) : select;
                });
        const div = bList ? Common.elem('ul', { id: `${elemId}_list` }, items) : Common.elem('div', { style: 'display: inline;' }, items);
        const children = [div];
        if (bSelect) {
            children.push(Common.elem('button', { class: 'warnsmall', onclick: () => Common.markAllCheckboxes(elemId, true) }, '[X]'));
            children.push(Common.text(Common.nbsp));
            children.push(Common.elem('button', { class: 'warnsmall', onclick: () => Common.markAllCheckboxes(elemId, false) }, `[${Common.nbsp}]`));
        }
        if (textElemId) {
            children.push(Common.text(Common.nbsp));
            children.push(Common.elem('button', { class: 'warnsmall', onclick: () => Common.fromCheckboxesToText(elemId, textElemId) }, '>>'));
        }
        elem.replaceChildren(...children);
    }
    static sortArray(list, bSort) {
        if (!bSort)
            return list;
        list.sort();
        return list;
    }
    static sortList(list, bSort) {
        function sortFunc(key1, key2) {
            const a = list[key1];
            const b = list[key2];
            const aa = (typeof a === 'string') ? a : `zz${key1}`;
            const bb = (typeof b === 'string') ? b : `zz${key2}`;
            return aa.localeCompare(bb);
        }
        if (!bSort)
            return Object.keys(list);
        const keys = Object.keys(list).sort((key1, key2) => { return sortFunc(key1, key2); });
        return keys;
    }
    /**
     * @static
     * @param {string} elemId
     * @param {boolean} val
     */
    static markAllCheckboxes(elemId, val) {
        const elem = document.getElementById(elemId);
        if (!elem)
            return;
        const inputs = elem.getElementsByTagName('input');
        for (const input of inputs) {
            if (input.type != 'checkbox')
                continue;
            input.checked = val;
        }
    }
    /**
     * @static
     * @param {string} elemId
     * @param {string} elemTextId
     * @param {string} [allLabel]
     */
    static fromCheckboxesToText(elemId, elemTextId, allLabel) {
        const elem = document.getElementById(elemId);
        if (!elem)
            return;
        const elemText = Common.getInputElement(elemTextId);
        if (!elemText)
            return;
        let bAll = true;
        let t = '';
        const inputs = elem.getElementsByTagName('input');
        for (const input of inputs) {
            if (input.type != 'checkbox')
                continue;
            if (!input.checked) {
                bAll = false;
                continue;
            }
            if (t)
                t += ', ';
            t += input.parentElement?.innerText.trim();
        }
        elemText.value = allLabel && bAll ? allLabel : t;
    }
    /**
     * Extracts the query string variables and returns them in a key-value object
     * @returns {MapOfStrings}
     * @static
     * @memberof Common
     */
    static getUrlVars() {
        const vars = {};
        const matches = window.location.search.matchAll(/[?&]+([^=&]+)=([^&]*)/gi);
        for (const match of matches) {
            vars[match[1]] = decodeURIComponent(match[2]);
        }
        return vars;
    }
    /**
     *
     * @param {string} [info]
     * @returns {string[]}
     * @static
     * @memberof Common
     */
    static parseStoresInfo(info) {
        const arr = [];
        if (!info)
            return arr;
        const lines = info.split('\n');
        let val = '';
        for (const line of lines) {
            switch (line.trim()) {
                case '-':
                    break;
                case '':
                    if (val)
                        arr.push(val);
                    val = '';
                    break;
                default:
                    if (val)
                        val += '\n';
                    val += line;
            }
        }
        if (val)
            arr.push(val);
        return arr;
    }
    /**
     *
     * @param {string} url
     * @returns {boolean}
     * @static
     * @memberof Common
     */
    static isInStorage(url) {
        return url && url.startsWith('https://firebasestorage');
    }
    /**
     *
     * @param {string} elemId
     * @static
     * @memberof Common
     */
    static photoOnChange(elemId) {
        const inputElem = Common.getInputElement(elemId);
        if (!inputElem)
            return;
        const imgElem = Common.getImageElement(`img_${elemId}`);
        if (imgElem) {
            imgElem.removeAttribute('src');
            if (inputElem.value)
                imgElem.src = inputElem.value;
        }
        const btnElem = document.getElementById(`btn_${elemId}`);
        if (btnElem && btnElem.firstElementChild && btnElem.firstElementChild instanceof HTMLImageElement) {
            btnElem.firstElementChild.src = Common.isInStorage(inputElem.value) ? photoicon : photoaddicon;
        }
    }
    /**
     *
     * @param {string | null} uid
     * @param {string | null} name
     * @param {string | null} email
     * @returns {string}
     * @static
     * @memberof Common
     */
    static getUserTitle(uid, name, email) {
        return name == email || !name ? (email || uid || '') : `${name} (${email})`;
    }
    /**
     *
     * @param {string | number | Date | { seconds: number }} dat
     * @returns {string}
     * @static
     * @memberof Common
     */
    static formatDate(dat) {
        return !dat ? '' : (typeof dat == 'object' && 'seconds' in dat) ? new Date(dat.seconds * 1000).toLocaleString() : new Date(dat).toLocaleString();
    }
    /**
     *
     * @param {{code: string}} e
     * @returns {string | {code: string}}
     * @static
     * @memberof Common
     */
    static getError(e) {
        switch (e.code) {
            case 'permission-denied':
            case 'storage/unauthorized':
                return Common.res.errorAccessDenied;
            case 'auth/popup-closed-by-user':
                return Common.res.errorAuthCancelled;
            case 'auth/popup-blocked':
                return Common.res.errorPopupBlocked;
            case 'not-found':
                return Common.res.errorNotFound;
            default:
                return e;
        }
    }
    /**
     * @static
     * @readonly
     * @memberof Common
     */
    static get PAGE_SIZE() {
        return Common.constConfigs.pageSize;
    }
    /**
     *
     * @param {number} cnt
     * @returns {number}
     * @static
     * @memberof Common
     */
    static getPagesCount(cnt) {
        return cnt == 0 ? 1 : Math.ceil(cnt / Common.PAGE_SIZE);
    }
    /**
     *
     * @param {string} userId
     * @param {string} recordId
     * @static
     * @memberof Common
     */
    static async notifyForRequest(userId, recordId) {
        if (!Common.notifyForRequestFunc)
            return;
        await Common.notifyForRequestFunc({ userId, recordId });
    }
    // static async tryDbAccess() {
    //     const deleteRef = doc(Common.db, Collections.usersCollection, 'delete');
    //     await deleteDoc(deleteRef);
    // }
    /**
     * tryDbAccess attempts to delete a document with the accessType parameter in the accessCollection
     *
     * @static
     * @async
     * @param {('admin' | 'operator')} accessType - The type of access to attempt to delete
     * @returns {Promise<boolean>} - A promise that resolves to true if the deletion was successful, false otherwise
     * @memberof Common
     */
    static async tryDbAccess(accessType) {
        try {
            const accessRef = doc(Common.db, "access" /* Collections.accessCollection */, accessType);
            await deleteDoc(accessRef);
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * getDbAccess attempts to retrieve a document containing access information of a user with the userId parameter
     *
     * @static
     * @async
     * @param {string} userId - The id of the user to get access information for
     * @returns {Promise<number>} - A promise that resolves to the level of access of the user, 0 if the user does not exist, or an error occurs
     * @memberof Common
     */
    static async getDbAccess(userId) {
        try {
            const accessRef = doc(Common.db, "access" /* Collections.accessCollection */, userId);
            const accessDoc = await getDoc(accessRef);
            if (accessDoc.exists())
                return accessDoc.data().level;
            else
                return 0;
        }
        catch {
            return 0;
        }
    }
    /**
     * fillUser fills the Common.user object with the information provided in the userInfo parameter
     *
     * @static
     * @async
     * @param {(UserInfo | null)} userInfo - The user information to fill the Common.user object with
     * @memberof Common
     */
    static async fillUser(userInfo) {
        if (!userInfo) {
            Common.user.userId = null;
            Common.user.userName = '';
            Common.user.userEmail = '';
            Common.user.userAdmin = -1;
            Common.user.userVoice = '';
            return;
        }
        Common.user.userId = userInfo.uid;
        Common.user.userName = userInfo.displayName;
        Common.user.userEmail = userInfo.email;
        Common.user.userAdmin = 0;
        Common.user.userVoice = await Common.getUserVoiceTo(Common.user.userId);
        try {
            Common.user.userOperator = await Common.tryDbAccess('operator');
            if (!Common.user.userOperator) {
                if (!await Common.tryDbAccess('admin'))
                    Common.user.userAdmin = -1;
                else
                    Common.user.userAdmin = await this.getDbAccess(Common.user.userId);
            }
            else {
                await Common.createUserIfMissing();
            }
        }
        catch {
            Common.user.userAdmin = -1;
        }
    }
    static buildDropdownDiv(moduleName, divId, prefix, label, textElemId = '') {
        let t = `
<div id="${divId}" class="dropdown" onclick="event.stopPropagation()">
    <button id="${prefix}DropdownBtn" onclick="lib.toggleDropdown('${moduleName}', '${prefix}')" class="dropbutton">${label}...</button>
    <span id="${prefix}DropdownName" class="dropspan"></span>
    <div id="${prefix}DropdownDiv" class="dropdown-content">
        <input type="text" placeholder="${Common.res.lblSearch}..." id="${prefix}DropdownSearch" class="dropsearch" autocomplete="off">
        <div id="${prefix}DropdownItemsDiv"></div>
    </div>
</div>`;
        if (textElemId) {
            t += `<button class="warnsmall" style="margin-left: 10px;" onclick="document.getElementById('${textElemId}').value = document.getElementById('${prefix}DropdownName').innerText">&gt;&gt;</button>`;
        }
        return t;
    }
    static showDropdownButton(prefix, bShow) {
        Common.displayElem(`${prefix}DropdownBtn`, bShow, 'inline-block');
    }
    static buildCheckbox(elemId, label, onchange = '', bSmall = false) {
        const small = bSmall ? ' inline small' : '';
        return `
<label class="checkbox-container${small}">${label}
    <input id="${elemId}" type="checkbox" ${Common.iif(onchange != '', `onchange="${onchange}"`, '')}>
    <span class="checkmark${small}"></span>
</label>`;
    }
    static sanitize(text) {
        return sanitizeHtml(text, {
            allowedTags: [],
            allowedAttributes: {}
        });
    }
    static elem(tag, attributes = null, child = []) {
        const elem = document.createElement(tag);
        if (attributes) {
            const keys = Object.keys(attributes);
            for (const key of keys) {
                const attr = attributes[key];
                if (key.startsWith('on'))
                    elem.addEventListener(key.slice(2), attr);
                else if (attr === null)
                    elem.removeAttribute(key);
                else if (typeof attr === 'string')
                    elem.setAttribute(key, attr);
                else if (typeof attr === 'number')
                    elem.setAttribute(key, String(attr));
            }
        }
        Common.appendChild(elem, child);
        return elem;
    }
    static text(text) {
        return document.createTextNode(text);
    }
    static appendChild(elem, child) {
        if (child && Array.isArray(child)) {
            child.forEach((item) => { if (item)
                elem.appendChild(item); });
        }
        else if (child && child instanceof Node) {
            elem.appendChild(child);
        }
        else {
            elem.appendChild(Common.text(child || Common.nbsp));
        }
    }
    static async toPage(moduleName, newpage) {
        (await Common.module(moduleName)).toPage(newpage);
    }
    static async edit(moduleName, id) {
        (await Common.module(moduleName)).edit(id);
    }
    static async viewProduct(productId) {
        await Common.goToPage("products" /* Modules.products */, `productId=${productId}`);
    }
    static async viewIngredient(ingredientId) {
        await Common.goToPage("ingredients" /* Modules.ingredients */, `ingredientId=${ingredientId}`);
    }
    static async chooseDropdown(moduleName, prefix, val, key = '') {
        await (await Common.module(moduleName)).chooseDropdown(prefix, val, key);
        const dropdownDiv = document.getElementById(`${prefix}DropdownDiv`);
        if (dropdownDiv)
            dropdownDiv.classList.remove('show');
        Common.curDropdownDiv = null;
        Common.toElem(val, `${prefix}DropdownName`);
    }
    static async filterPage(moduleName, filter) {
        await Common.goToPage(moduleName, `filter=${filter}`);
    }
    static async openUrl(elemId) {
        const elem = Common.getInputElement(elemId);
        if (!elem)
            return;
        if (!elem.value)
            return;
        if (!elem.value.startsWith('http'))
            return;
        const tab = window.open(elem.value, '_blank');
        if (tab)
            tab.focus();
    }
    static getStoresArrString(arr) {
        return arr && Array.isArray(arr) ? arr.join('\n----- ----- ----- ----- -----\n') : Common.res.lblNoStoreAddresses;
    }
    static async createUserIfMissing() {
        const userId = Common.user.userId;
        if (!userId)
            return;
        const userRef = doc(Common.db, "users" /* Collections.usersCollection */, userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists())
            return;
        const userData = {
            name: Common.user.userName,
            email: Common.user.userEmail,
        };
        try {
            await setDoc(doc(Common.db, "users" /* Collections.usersCollection */, userId), userData);
        }
        catch (e) {
            alert(Common.res.errorCurrentUserCreate + Common.getError(e));
            return;
        }
    }
    // ------------------------------ modules utils ------------------------------
    static async isUnique(unique) {
        try {
            const q = query(collection(Common.db, "users_data" /* Collections.usersDataCollection */), where('unique', '==', unique), limit(1));
            const snapshot = await getDocs(q);
            return snapshot.empty;
        }
        catch (e) {
            alert(Common.getError(e));
            return false;
        }
    }
    static async checkBrandInCollection(collectionName, brandId) {
        try {
            const col = collectionName == "records" /* Collections.recordsCollection */ ? collectionGroup(Common.db, "records" /* Collections.recordsCollection */) : collection(Common.db, "products" /* Collections.productsCollection */);
            const q = query(col, where('brandID', '==', brandId), limit(1));
            const snapshot = await getDocs(q);
            return !snapshot.empty;
        }
        catch (e) {
            alert(format(Common.res.errorBrandCheckFormat, [collectionName, Common.getError(e)]));
            return true;
        }
    }
    static async updateBrandInCollection(collectionName, brandId, json) {
        try {
            const col = collectionName == "records" /* Collections.recordsCollection */ ? collectionGroup(Common.db, "records" /* Collections.recordsCollection */) : collection(Common.db, collectionName);
            const q = query(col, where('brandID', '==', brandId));
            const snapshot = await getDocs(q);
            snapshot.forEach(async (document) => {
                try {
                    const data = document.data();
                    let changed = false;
                    if (data.brand != json.brand) {
                        data.brand = json.brand;
                        changed = true;
                    }
                    if (collectionName == "products" /* Collections.productsCollection */ && data.descr_brand != json.descr_brand) {
                        data.descr_brand = json.descr_brand;
                        changed = true;
                    }
                    if (collectionName == "products" /* Collections.productsCollection */ && data.stores_info != json.stores_info) {
                        data.stores_info = json.stores_info;
                        data.stores_arr = json.stores_arr;
                        changed = true;
                    }
                    if (collectionName == "products" /* Collections.productsCollection */ && !data.stores_arr && json.stores_arr) {
                        data.stores_arr = json.stores_arr;
                        changed = true;
                    }
                    if (collectionName == "products" /* Collections.productsCollection */ && data.photo_logo != json.photo_brand) {
                        data.photo_logo = json.photo_brand;
                        changed = true;
                    }
                    if (collectionName == "products" /* Collections.productsCollection */ && (data.origin_country || '') != (json.origin_country || '')) {
                        data.origin_country = json.origin_country || '';
                        changed = true;
                    }
                    if (changed)
                        await updateDoc(document.ref, data);
                }
                catch (e) {
                    alert(format(Common.res.errorBrandDocUpdateFormat, [collectionName, document.id, Common.getError(e)]));
                }
            });
        }
        catch (e) {
            alert(format(Common.res.errorBrandUpdateFormat, [collectionName, Common.getError(e)]));
        }
    }
    static async updateProductIngredients(ingredientId, ingredientData) {
        if (!ingredientId)
            return;
        const q = query(collectionGroup(Common.db, "ingredients" /* Collections.ingredientsCollection */), where('ingredientID', '==', ingredientId));
        const snapshot = await getDocs(q);
        await Promise.all(snapshot.docs.map(async (ingredientDoc) => {
            const json = {
                ingredientID: ingredientId,
                ingredient: ingredientDoc.data().ingredient,
                name: ingredientData.name,
                functions: ingredientData.functions || '',
                functions_list: ingredientData.functions_arr || [],
                less1pct: ingredientData.less1pct ? true : false,
                group: ingredientData.group,
                groups_arr: ingredientData.groups_arr || [],
            };
            await updateDoc(ingredientDoc.ref, json);
        }));
    }
    static async checkIngredientInProducts(ingredientId) {
        if (!ingredientId)
            return;
        try {
            const q = query(collectionGroup(Common.db, "ingredients" /* Collections.ingredientsCollection */), where('ingredientID', '==', ingredientId), limit(1));
            const snapshot = await getDocs(q);
            return !snapshot.empty;
        }
        catch (e) {
            alert(Common.res.errorProductCheck + Common.getError(e));
            return true;
        }
    }
    static async checkProductInRecords(productId) {
        try {
            const q = query(collectionGroup(Common.db, "records" /* Collections.recordsCollection */), where('productID', '==', productId), limit(1));
            const snapshot = await getDocs(q);
            return !snapshot.empty;
        }
        catch (e) {
            alert(Common.res.errorProductCheck + Common.getError(e));
            return true;
        }
    }
    static async updateProductInRecords(productId, json) {
        try {
            const q = query(collectionGroup(Common.db, "records" /* Collections.recordsCollection */), where('productID', '==', productId), limit(1));
            const snapshot = await getDocs(q);
            snapshot.forEach(async (document) => {
                try {
                    const data = document.data();
                    let changed = false;
                    if (data.brand != json.brand) {
                        data.brand = json.brand;
                        changed = true;
                    }
                    if (data.brandID != json.brandID) {
                        data.brandID = json.brandID;
                        changed = true;
                    }
                    if (data.title != json.title) {
                        data.title = json.title;
                        changed = true;
                    }
                    if (data.state == 'wait' && !json.is_partial) {
                        data.state = 'ready';
                        data.processed = new Date().toISOString();
                        changed = true;
                    }
                    if (changed)
                        await updateDoc(document.ref, data);
                }
                catch (e) {
                    alert(format(Common.res.errorProductRecordUpdateFormat, [document.id, Common.getError(e)]));
                }
            });
        }
        catch (e) {
            alert(Common.res.errorProductUpdate + Common.getError(e));
        }
    }
    static async checkProductWithReviews(productId) {
        try {
            const q = query(collection(doc(Common.db, "products" /* Collections.productsCollection */, productId), "reviews" /* Collections.reviewsCollection */), limit(1));
            const snapshot = await getDocs(q);
            return !snapshot.empty;
        }
        catch (e) {
            alert(Common.res.errorProductCheck + Common.getError(e));
            return true;
        }
    }
    static async deleteProductRatings(productId) {
        try {
            const q = query(collection(doc(Common.db, "ratings" /* Collections.ratingsCollection */, productId), "user_ratings" /* Collections.userRatingsCollection */));
            const snapshot = await getDocs(q);
            await Promise.all(snapshot.docs.map(async (userRatingDoc) => {
                await deleteDoc(userRatingDoc.ref);
            }));
            const ratingRef = doc(Common.db, "ratings" /* Collections.ratingsCollection */, productId);
            await deleteDoc(ratingRef);
        }
        catch (e) {
            console.log("ratings" /* Collections.ratingsCollection */, Common.getError(e));
        }
    }
    static slideActionChanged(value) {
        Common.displayElem('slideActionProductTable', value == 'product', 'table');
        Common.displayElem('slideActionPubTable', value == 'pub', 'table');
    }
    static isLess1Pct(data) {
        const names = [
            'POLYSORBATE 20',
            'HYDROXYPROPYLCELLULOSE',
            'HYDROXYPROPYL METHYLCELLULOSE',
            'HYDROXYPROPYLMETHYL CELLULOSE',
            'HYDROXYETHYLCELLULOSE',
            'HYDROXYETHYL ETHYLCELLULOSE',
            'HYDROXYETHYL CELLULOSE',
            'HYDROXYBUTYL METHYLCELLULOSE',
            'DISODIUM EDTA',
            'SODIUM BENZOATE',
            // 'BENZYLPARABEN',
            // 'BUTYLPARABEN',
            // 'ETHYLPARABEN',
            // 'ISOBUTYLPARABEN',
            // 'ISOPROPYLPARABEN',
            // 'METHYLPARABEN',
            // 'PROPYLPARABEN',
            'SODIUM HYDROXIDE',
            // 'BUTYLENE GLYCOL',
            // 'METHYLPARABEN',
            'PHENOXYETHANOL',
            'POTASSIUM SORBATE',
        ];
        if (data.name && names.includes(data.name))
            return true;
        if (data.name?.includes(' GUM ') || data.name?.endsWith(' GUM') || data.name?.startsWith('GUM '))
            return true;
        if (data.name?.includes('PARABEN'))
            return true;
        if (data.functions == 'Perfuming' || data.functions == 'Fragrance' || data.functions == 'Fragrance, Perfuming')
            return true;
        // if (data.functions_arr) {
        //     if (data.functions_arr.length == 1 && data.functions_arr[0] == 'Perfuming') return true;
        //     if (data.functions_arr.length == 1 && data.functions_arr[0] == 'Fragrance') return true;
        //     if (data.functions_arr.length == 2 && data.functions_arr[0] == 'Fragrance' && data.functions_arr[1] == 'Perfuming') return true;
        // }
        return false;
    }
    static async getUserVoiceTo(userId) {
        try {
            const userRef = doc(Common.db, "users" /* Collections.usersCollection */, userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists())
                return userDoc.data().voice_call_to || '';
            else
                return '';
        }
        catch {
            return '';
        }
    }
    static async setVoiceCallTo(voiceTo) {
        try {
            await setDoc(doc(Common.db, "app_config" /* Collections.appConfigCollection */, 'arm'), { voice_call_to: voiceTo }, { merge: true });
        }
        catch (e) {
            alert(Common.getError(e));
            return true;
        }
    }
    static async voiceCallToMe(callToMe) {
        if (!Common.user.userVoice)
            return;
        await Common.setVoiceCallTo(callToMe ? Common.user.userVoice : '');
    }
    static async voiceCallTest() {
        if (!Common.user.userVoice)
            return;
        // const callUrl = `https://api.callmebot.com/text.php?text=Test+voice+call&user=${Common.user.userVoice}`;
        const callUrl = `https://api.callmebot.com/start.php?text=Test+voice+call&user=${Common.user.userVoice}`;
        const tab = window.open(callUrl, '_blank');
        if (tab)
            tab.focus();
    }
    static getVolume(line) {
        let matches;
        const ll = line.toLowerCase().replace(',', '.')
            // .replace(' 6 g\nnew', ' g')
            // .replace('3 pack - 10 x 2.27 oz cloths/ 3 pack - 10 x 67 ml cloths', '10 x 67 ml')
            // .replace('\nnew', '')
            // .replace('\nsale', '')
            // .replace('(refill)', '')
            // .replace('refill', '')
            // .replace(' oil', '')
            // .replace(' tube', '')
            // .replace('  pump', '')
            // .replace(' rollerball', '')
            // .replace(' extra mild', '')
            // .replace('starter kit', '')
            // .replace('hair and body mist', '')
            // .replace('eau de parfum spray', '')
            // .replace(' packets', '')
            // .replace('ml sheets', 'ml')
            // .replace('coconut', '')
            // .replace('oz pump', 'oz')
            // .replace('standard size - ', '')
            // .replace('mint chocolate ', '')
            // .replace('count -', 'x')
            // .replace('1.7 / 50', '1.7 oz/ 50 ml')
            .trim();
        if (!ll)
            return '';
        // if (!ll.match(/\d/) || ll.match(/^\d+\spacks?$/) || ll.match(/^\d+\ssheets?$/) || ll.match(/^\d+\scounts?$/)) return '';
        const mult = ll.match(/^(\d+ x )(.+)$/);
        const l = mult ? mult[2] : ll;
        const m = mult ? mult[1] : '';
        // if (l.includes('capsules') || l.includes('pads') || l.includes('wipes') || l.includes('treatment')
        //     || l.includes('vials') || l.includes('mask') || l.includes('heads') || l.includes('day')
        //     || l.includes('PACK') || l.includes('supply') || l.includes('pair') || l.includes('patch')
        //     || l.includes('packettes') || l.includes('dots') || l.includes('swabs') || l.includes('mitt')
        //     || l.includes('pcs') || l.includes('towel') || l.includes('heart') || l.includes('gummies')
        //     || l.includes('tablet') || l.includes('stick') || l.includes('ampoule') || l.includes('gummies')) return '';
        // 10 ml = 0.33814 fl oz (US)
        // 100 g = 3.5274 oz
        matches = l.match(/^([\d|.]+)\s?(?:fl[ |.])?oz\.?\s?[\\|/]\s?([\d|.]+)\s?ml$/i)
            || l.match(/^([\d|.]+)\s?(?:fl[ |.])?oz\.?\s?\(\s?([\d|.]+)\s?ml\)$/i);
        if (matches)
            return `${m}${matches[1]} oz / ${matches[2]} ml`;
        matches = l.match(/^([\d|.]+)\s?ml\s?[\\|/]\s?([\d|.]+)\s?(?:fl[ |.])?oz\.?$/i);
        if (matches)
            return `${m}${matches[2]} oz / ${matches[1]} ml`;
        matches = l.match(/^([\d|.]+)\s?(?:fl[ |.])?oz\.?\s?[\\|/]\s?([\d|.]+)\s?gm?$/i);
        if (matches)
            return `${m}${matches[1]} oz / ${matches[2]} g`;
        matches = l.match(/^([\d|.]+)\s?oz\.?$/i);
        if (matches)
            return `${m}${matches[1]} oz`;
        matches = l.match(/^([\d|.]+)\s?ml$/i);
        if (matches) {
            const ml = parseFloat(matches[1]);
            const oz = ml % 3 == 0 ? Common.round(ml * 0.033333333333) : Common.round(ml * 0.033814);
            return `${m}${oz} oz / ${matches[1]} ml`;
        }
        matches = l.match(/^([\d|.]+)\s?gm?$/i);
        if (matches) {
            const g = parseFloat(matches[1]);
            const oz = Common.round(g * 0.035274);
            return `${m}${oz} oz / ${matches[1]} g`;
        }
        return line;
        // return '?';
    }
    static round(num) {
        return Math.round(num * 100) / 100;
    }
    static formatVolume(elemId) {
        const input = Common.getInputElement(elemId);
        if (!input)
            return;
        const oldVolume = input.value;
        const newVolume = Common.getVolume(oldVolume);
        if (oldVolume == newVolume)
            return;
        input.value = newVolume;
    }
    static save(filename, data) {
        const blob = new Blob([data], { type: 'text/csv' });
        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = filename;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
    }
    static arraysEqual(array1, array2) {
        if (array1.length != array2.length)
            return false;
        return array1.every((value, i) => value === array2[i]);
    }
    static sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    static async readIngroups() {
        if (Common.ingroupsLoaded)
            return;
        const snapshot = await getDocs(collection(Common.db, "ingroups" /* Collections.ingroupsCollection */));
        Common.ingroups = {};
        snapshot.docs.forEach((ingroupDoc) => {
            Common.ingroups[ingroupDoc.id] = ingroupDoc.data();
        });
        Common.ingroupsLoaded = true;
    }
}
/**
 * The non-breaking space character
 *
 * @static
 * @type {string}
 */
Common.nbsp = '\xa0';
/**
 * The ID of all items
 *
 * @static
 * @type {string}
 */
Common.allID = '0';
/**
 * The attention message
 *
 * @static
 * @type {string}
 */
Common.attention = 'ATTENTION';
/**
 * The alternate attention message
 *
 * @static
 * @type {string}
 */
Common.attention2 = 'ATTENTION2';
/**
 * The map of loaded modules
 *
 * @static
 * @type {Map<Modules, Module>}
 */
Common.modules = new Map();
/**
 * The current user
 *
 * @static
 * @type {User}
 */
Common.user = {
    userId: null,
    userName: null,
    userEmail: null,
    userNickname: null,
    userAdmin: -1,
    userOperator: false,
    userVoice: '',
};
/**
 * The current page
 *
 * @static
 * @type {(Modules | null)}
 * @memberof Common
 */
Common.currentPage = null;
/**
 * The current dropdown div element
 *
 * @static
 * @type {(HTMLElement | null)}
 * @memberof Common
 */
Common.curDropdownDiv = null;
Common.ingroups = {};
Common.ingroupsLoaded = false;
